/** @jsx jsx */
import React, { useRef, useState, useEffect, type ReactNode } from 'react';
import { css, styled as styled2, jsx, keyframes as keyframes2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { keyframes } from 'styled-components';
import Button, { ButtonGroup } from '@atlaskit/button';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import { ModalFooter } from '@atlaskit/modal-dialog';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';

import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import messages from './messages.tsx';

type Props = {
	editLink: string;
	link: string;
	onCancel: () => void;
	intl: IntlShape;
};

type FadeProps = {
	show: boolean;
	children: ReactNode;
};

const FadeOut = ({ show, children }: FadeProps) => {
	const [shouldRender, setRender] = useState(show);

	useEffect(() => {
		if (show) setRender(true);
	}, [show]);

	const onAnimationEnd = () => {
		if (!show) setRender(false);
	};

	if (shouldRender) {
		return fg('jsm_migrated_styled_component_18081') ? (
			<div css={[!show && fadeOutAnimationStyle]} onAnimationEnd={onAnimationEnd}>
				{children}
			</div>
		) : (
			<FadeWrapper show={show} onAnimationEnd={onAnimationEnd}>
				{children}
			</FadeWrapper>
		);
	}
	return null;
};

export const Footer = ({ onCancel, editLink, link, intl }: Props) => {
	const linkTextArea = useRef<unknown>(null);
	const [copied, setCopied] = useState(false);

	const copyToClipboard = () => {
		if (linkTextArea.current !== null) {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			linkTextArea.current.select();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.execCommand('copy');
			setCopied(true);
			setTimeout(() => {
				setCopied(false);
			}, 1000);
		}
	};

	return (
		<ModalFooter>
			<Container>
				<ButtonGroup>
					<Button appearance="default" href={editLink} target="_blank">
						{intl.formatMessage(messages.editPage)}
					</Button>
					<Textfield value={link} isReadOnly isCompact ref={linkTextArea} />
					<Button appearance="default" onClick={() => copyToClipboard()}>
						{intl.formatMessage(messages.copyLink)}
					</Button>
					<IconWrapper>
						<FadeOut show={copied}>
							<CheckCircleOutlineIcon
								primaryColor={token('color.icon.success', colors.G300)}
								size="medium"
								label=""
							/>
						</FadeOut>
					</IconWrapper>
					<Button appearance="subtle" onClick={() => onCancel()}>
						{intl.formatMessage(messages.cancel)}
					</Button>
				</ButtonGroup>
			</Container>
		</ModalFooter>
	);
};

export default injectIntl(Footer);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled2.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 2}px`,
});

const fadeOut = keyframes({
	from: {
		opacity: 1,
	},
	to: {
		opacity: 0,
	},
});

const fadeOut2 = keyframes2({
	from: {
		opacity: 1,
	},
	to: {
		opacity: 0,
	},
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FadeWrapper = styled.div<{ show: any }>`
	animation: ${
			(props: any) => (!props.show ? fadeOut : '') // eslint-disable-line @typescript-eslint/no-explicit-any
		}
		0.8s linear;
`;

const fadeOutAnimationStyle = css({
	animation: `${fadeOut2}0.8s linear`,
});
